import { css } from '@emotion/css'
import { Stack } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Typography from '@mui/material/Typography'
import classNames from 'classnames'
import { observer } from 'mobx-react'
import React, { useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { queryClient } from '../../App'
import { isAdmin, isSuperOperator } from '../../services/AccountService'
import { notifySuccess } from '../../services/ToastifyService/ToastifyService'
import { closeModalFn } from '../../utils/controllers/ModalController'
import DealerSelect from '../../views/RequestsManagement/components/RequestDetails/AssignToDealerView/utils/DealerSelect'
import FormAutocomplete from '../../views/SingleRequestPage/components/SingleRequestDrawer/elements/FormAutocomplete'
import { useToReworkDrawer } from '../../views/SingleServiceRequest/hooks/useToReworkDrawer'
import InputField from '../customElements/InputField/InputField'
import FormField from '../Form/FormField'

const ReportChangeStatus = ({
  store,
  currentServiceData,
  closeChange,
  selectedTab,
}) => {
  const status = currentServiceData?.status
  const intl = useIntl()
  const {
    setComment,
    comment,
    contentState,
    newStatus,
    markAsImpossible,
    assignDealer,
    markAsRejectedByUser,
    backToSCAfterRevision,
    markServiceOrderAsDuplicateFunc,
    updateCurrentServiceData,
    gerReworkPagedData,
    updateOrders,
    handleReLoad,
  } = store
  const {
    watch,
    isLoading,
    disabled,
    control,
    impossibleReasons,
    showExplanation,
    onSubmit,
  } = useToReworkDrawer('to-impossible', currentServiceData?.id, true)

  useEffect(() => {
    store.newStatus = null
    store.comment = ''
  }, [])

  const handleChangeStatus = async () => {
    closeChange()
    switch (newStatus) {
      case 7:
        const res = await onSubmit()
        if (selectedTab === 3) {
          closeModalFn['common-service-drawer']?.()
          gerReworkPagedData()
        } else {
          if (isSuperOperator()) {
            updateOrders([res])
            updateCurrentServiceData(res)
          } else {
            closeModalFn['common-service-drawer']?.()
            handleReLoad()
          }
        }
        notifySuccess('Заявка обновлена')
        store.newStatus = null
        store.comment = ''
        break
      case 2:
        await assignDealer(isAdmin())
        if (isAdmin() && !!currentServiceData) {
          queryClient.invalidateQueries({ queryKey: ['service-table'] })
          queryClient.invalidateQueries({
            queryKey: ['get-service-history'],
          })
        }
        store.newStatus = null
        store.comment = ''
        break
      case 10:
        await markServiceOrderAsDuplicateFunc({ top: true })
        if (isAdmin() && !!currentServiceData) {
          queryClient.invalidateQueries({ queryKey: ['service-table'] })
          queryClient.invalidateQueries({
            queryKey: ['get-service-history'],
          })
        }
        store.newStatus = null
        store.comment = ''
        break

      case 21:
        await backToSCAfterRevision()
        if (isAdmin() && !!currentServiceData) {
          queryClient.invalidateQueries({ queryKey: ['service-table'] })
          queryClient.invalidateQueries({
            queryKey: ['get-service-history'],
          })
        }
        store.newStatus = null
        store.comment = ''
        break

      default:
        break
    }
  }

  return (
    <Stack
      name={'content'}
      mx={2}
      className={classNames('fx fx-col', css({ flex: '1 1 auto' }))}
      my={1}
      spacing={'16px'}
    >
      <Box mb={2}>
        <Typography sx={{ fontSize: '14px' }} variant={'h6'}>
          <FormattedMessage id={'Изменить статус'} />
        </Typography>
      </Box>
      <FormControl fullWidth>
        <InputLabel id='demo-simple-select-label'>
          <FormattedMessage id={'Статус'} />
        </InputLabel>
        <Select
          value={newStatus}
          label={intl.formatMessage({ id: 'Статус' })}
          onChange={(event) => (store.newStatus = event.target.value)}
        >
          {store
            .availableStatusesToChange(isAdmin(), status, selectedTab)
            .map((el) => (
              <MenuItem key={el.id} value={el.id}>
                <Box className={'aln-center'}>
                  <img alt={''} src={el.imageSrc} />
                  <Box ml={1}>
                    <Typography variant={'subtitle2'}>{el.name}</Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
        </Select>
      </FormControl>
      {[30].includes(newStatus) ||
      (newStatus === 10 && ![7, 11].includes(status)) ? (
        <Box mt={2}>
          <InputField
            multiline
            autoFocus
            value={comment}
            onChange={setComment}
            rows={4}
            fullWidth
            placeholder={intl.formatMessage({ id: 'Введите комментарий' })}
          />
        </Box>
      ) : null}
      {newStatus === 2 && isAdmin() && store?.serviceStore && (
        <Box mt={1}>
          <DealerSelect currentServiceData={currentServiceData} store={store} />
        </Box>
      )}
      {newStatus === 7 && (
        <>
          <FormAutocomplete
            control={control}
            fullWidth
            name='impossibleReasonId'
            options={impossibleReasons?.map((reason) => reason.name) ?? []}
            label='Причина'
            error={!watch('impossibleReasonId')}
            withClearBtn
            withTooltip
          />
          {showExplanation && (
            <FormField
              control={control}
              name='impossibleReasonCustomDescription'
              fullWidth
              label='Описание'
              multiline
              rows={2}
              error={!watch('impossibleReasonCustomDescription').trim()}
            />
          )}
        </>
      )}
      {contentState === 'changeRechecking' ? null : (
        <Box mt={2}>
          <Button
            fullWidth
            variant='contained'
            onClick={handleChangeStatus}
            disabled={(newStatus === 7 && disabled) || !newStatus}
            loading={isLoading}
          >
            <FormattedMessage id={'Подтвердить'} />
          </Button>
        </Box>
      )}
    </Stack>
  )
}

export default observer(ReportChangeStatus)
